/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, FunctionComponent } from "react";

import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  ConditionData,
  ConditionFieldProps,
  DEFAULT_TIMEZONE,
} from "@advapacs/common";
import { getWeekDays } from "../helpers";
import {
  BETWEEN,
  NOT_BETWEEN,
  DAY_OF_WEEK,
  EQUAL,
  NOT_EQUAL,
  getSelectStyles,
} from "../../../constant";
import TimezoneSelect, { allTimezones } from "react-timezone-select";

const Day: FunctionComponent<ConditionFieldProps> = ({
  disabled,
  id,
  errors,
  parent,
  condition,
  isHasScope = true,
  onValueChangeHandler,
  isDark,
}: ConditionFieldProps) => {
  const { t } = useTranslation("rule");
  const options = [
    {
      label: t("equals"),
      value: EQUAL,
    },
    {
      label: t("not_equals"),
      value: NOT_EQUAL,
    },
    {
      label: t("between"),
      value: BETWEEN,
    },
    {
      label: t("not_between"),
      value: NOT_BETWEEN,
    },
  ];

  const weekDayOptions = useMemo(() => getWeekDays(t), [t]);

  const dispatchNewValue = (con: ConditionData) => {
    onValueChangeHandler(
      {
        type: DAY_OF_WEEK,
        scope: isHasScope ? "conditions" : undefined,
        id,
        dayOfWeek: con,
      },
      parent
    );
  };

  const updateValue = (update: any) => {
    dispatchNewValue({
      ...condition,
      value: update?.value,
    });
  };

  const updateFrom = (update: any) => {
    const fromIndex = weekDayOptions.findIndex(
      (opt) => opt.value === update?.value
    );
    const toIndex = weekDayOptions.findIndex(
      (opt) => opt.value === condition?.to
    );
    dispatchNewValue({
      ...condition,
      to: fromIndex < toIndex ? condition?.to : undefined,
      from: update?.value,
    });
  };

  const updateTo = (update: any) => {
    dispatchNewValue({
      ...condition,
      to: update?.value,
    });
  };

  const selectOperator = (update: any) => {
    dispatchNewValue({ ...condition, operator: update?.value });
  };

  const hasError = () => {
    return (
      errors.find((item) => item.id === id && item.type === DAY_OF_WEEK) !==
      undefined
    );
  };

  return (
    <div className="row">
      <div className="col-md-3 col-sm-12">
        <div className="mb-1">
          <TimezoneSelect
            name="timeZone"
            onChange={(val) => {
              dispatchNewValue({
                ...condition,
                timeZone: val.value,
              });
            }}
            styles={getSelectStyles(isDark)}
            timezones={allTimezones}
            value={condition?.timeZone || DEFAULT_TIMEZONE.value}
            isSearchable
            isDisabled={disabled}
          />
        </div>
      </div>
      <div className="col-md-3 col-sm-12">
        <div className="mb-1">
          <Select
            styles={getSelectStyles(isDark)}
            menuPlacement="auto"
            menuPosition="fixed"
            isDisabled={disabled}
            name="operator"
            id={`${id}_operator`}
            classNamePrefix="form-control-select"
            options={options}
            placeholder={t("select_operator")}
            onChange={selectOperator}
            value={options.find(
              (option) => option?.value === condition?.operator
            )}
          />
          {hasError() && !condition?.operator && (
            <label
              className="validation-invalid-label"
              htmlFor={`${id}_operator`}
            >
              {t("field_required")}
            </label>
          )}
        </div>
      </div>
      {condition?.operator &&
        [EQUAL, NOT_EQUAL].includes(condition?.operator) && (
          <div className="col-md-3 col-sm-12">
            <div className="mb-1">
              <Select
                menuPosition="fixed"
                isDisabled={disabled}
                styles={getSelectStyles(isDark)}
                id={`${id}_value`}
                name="value"
                placeholder={t("select_day")}
                options={weekDayOptions}
                onChange={updateValue}
                value={weekDayOptions.find(
                  (option) => option?.value === condition?.value
                )}
              />
              {hasError() && !condition?.value && (
                <label
                  className="validation-invalid-label"
                  htmlFor={`${id}_value`}
                >
                  {t("field_required")}
                </label>
              )}
            </div>
          </div>
        )}
      {condition?.operator &&
        [BETWEEN, NOT_BETWEEN].includes(condition?.operator) && (
          <>
            <div className="col-md-3 col-sm-12">
              <div className="mb-1">
                <Select
                  isDisabled={disabled}
                  styles={getSelectStyles(isDark)}
                  id={`${id}_from`}
                  name="from"
                  placeholder={t("select_day")}
                  options={weekDayOptions}
                  onChange={updateFrom}
                  value={weekDayOptions.find(
                    (option) => option?.value === condition?.from
                  )}
                />
                {hasError() && !condition?.from && (
                  <label
                    className="validation-invalid-label"
                    htmlFor={`${id}_from`}
                  >
                    {t("field_required")}
                  </label>
                )}
              </div>
            </div>
            <div className="col-md-3 col-sm-12">
              <div className="mb-1">
                <Select
                  styles={getSelectStyles(isDark)}
                  isDisabled={disabled}
                  id={`${id}_to`}
                  name="to"
                  placeholder={t("select_day")}
                  options={weekDayOptions.map((opt, index) => {
                    const fromIndex = weekDayOptions.findIndex(
                      (opt2) => opt2.value === condition?.from
                    );
                    return {
                      ...opt,
                      isDisabled: fromIndex >= 0 && fromIndex >= index,
                    };
                  })}
                  onChange={updateTo}
                  value={
                    weekDayOptions.find(
                      (option) => option?.value === condition?.to
                    ) || null
                  }
                />
                {hasError() && !condition?.to && (
                  <label
                    className="validation-invalid-label text-danger"
                    htmlFor={`${id}_to`}
                  >
                    {t("field_required")}
                  </label>
                )}
              </div>
            </div>
          </>
        )}
    </div>
  );
};

export default Day;
